/*
 * Zoom in place
 *
 * Zooms a product image where it sits, with no lightbox: pinch with two
 * fingers, or double-tap (double-click with a mouse) to jump to a fixed zoom
 * centred on the point touched. Drag to pan while zoomed, and double-tap
 * again, or pinch back down, to reset.
 *
 * The frame is focusable, so the same is reachable from a keyboard: Enter or
 * Space toggles the zoom, plus and minus step it, the arrow keys pan, and
 * Escape resets. This matters more than usual here, because the control that
 * would open the media modal is hidden in this mode.
 *
 * Single-finger gestures are left to the browser until the image is zoomed, so
 * swiping the gallery and scrolling the page keep working. `touch-action` is
 * `pan-x pan-y` at rest, which still denies the browser the pinch so this can
 * handle it, and becomes `none` only while zoomed.
 *
 * Switched on per section by the "Zoom in place" choice on the Image zoom
 * setting.
 */

(function () {
  const IMAGE_SELECTOR = '.image-magnify-in_place';
  const MAX_SCALE = 4;
  const DOUBLE_TAP_SCALE = 2.5;
  const DOUBLE_TAP_MS = 300;
  const TAP_SLOP = 12;
  const KEY_ZOOM_STEP = 0.5;
  const KEY_PAN_STEP = 40;

  if (!window.PointerEvent) return;

  // Both product sections include this file, and each tag runs it again.
  if (window.imageZoomInPlaceLoaded) return;
  window.imageZoomInPlaceLoaded = true;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
  const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

  const instances = new Set();

  // Zooming pushes a history entry so the phone back button, and the iOS
  // back swipe, close the zoom instead of leaving the product page. Only one
  // image is ever zoomed, so one entry is enough.
  let historyOwner = null;
  let unwindingHistory = false;

  function syncHistory(instance) {
    if (instance.zoomed) {
      if (historyOwner) return;
      historyOwner = instance;
      try {
        window.history.pushState({ inPlaceZoom: true }, '');
      } catch (error) {
        // some embedded contexts refuse pushState; the zoom still works
        historyOwner = null;
      }
      return;
    }

    // closed by a gesture, the badge or Escape, so take our entry back out
    if (historyOwner !== instance) return;
    historyOwner = null;
    unwindingHistory = true;
    try {
      window.history.back();
    } catch (error) {
      unwindingHistory = false;
    }
  }

  class InPlaceZoom {
    constructor(image) {
      this.image = image;
      this.frame = image.closest('.product__media') || image.parentElement;
      this.container = image.closest('.product-media-container') || this.frame;
      this.badge = this.container.querySelector('.product__media-icon--in_place');
      this.pointers = new Map();
      this.scale = 1;
      this.offsetX = 0;
      this.offsetY = 0;
      this.lastTapAt = 0;
      this.pinch = null;
      this.pan = null;
      // a tap only counts when it was the only finger down for the whole gesture
      this.multiTouch = false;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.onBadgeClick = this.onBadgeClick.bind(this);

      this.frame.classList.add('in-place-zoom');
      this.frame.setAttribute('tabindex', '0');
      this.frame.setAttribute('role', 'button');
      this.frame.setAttribute('aria-pressed', 'false');

      this.frame.addEventListener('pointerdown', this.onPointerDown);
      this.frame.addEventListener('pointermove', this.onPointerMove);
      this.frame.addEventListener('pointerup', this.onPointerUp);
      this.frame.addEventListener('pointercancel', this.onPointerUp);
      this.frame.addEventListener('keydown', this.onKeyDown);
      this.frame.addEventListener('dragstart', this.onDragStart);
      if (this.badge) this.badge.addEventListener('click', this.onBadgeClick);

      instances.add(this);
    }

    get zoomed() {
      return this.scale > 1.01;
    }

    get centre() {
      const rect = this.frame.getBoundingClientRect();
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }

    // The magnifying glass badge sits over the frame as a sibling, so its
    // taps never reach the gesture handlers. It is the obvious thing to press,
    // so it toggles the zoom itself.
    onBadgeClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const centre = this.centre;
      this.toggle(centre.x, centre.y);
    }

    onDragStart(event) {
      if (this.zoomed) event.preventDefault();
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      this.pointers.set(event.pointerId, {
        startX: event.clientX,
        startY: event.clientY,
        x: event.clientX,
        y: event.clientY,
        movedBy: 0,
      });

      if (this.pointers.size > 1) this.multiTouch = true;

      if (this.pointers.size === 2) {
        this.startPinch();
      } else if (this.pointers.size === 1 && this.zoomed) {
        this.startPan(event.clientX, event.clientY);
        try {
          this.frame.setPointerCapture(event.pointerId);
        } catch (error) {
          // capture is a convenience; panning still works without it
        }
      }
    }

    onPointerMove(event) {
      const pointer = this.pointers.get(event.pointerId);
      if (!pointer) return;

      pointer.x = event.clientX;
      pointer.y = event.clientY;
      pointer.movedBy = Math.max(
        pointer.movedBy,
        Math.hypot(event.clientX - pointer.startX, event.clientY - pointer.startY)
      );

      if (this.pointers.size === 2 && this.pinch) {
        const [first, second] = [...this.pointers.values()];
        const centre = midpoint(first, second);
        const next = clamp(this.pinch.scale * (distance(first, second) / this.pinch.distance), 1, MAX_SCALE);
        this.scaleTo(next, centre.x, centre.y);
        event.preventDefault();
        return;
      }

      if (this.pan && this.zoomed) {
        this.offsetX = this.pan.offsetX + (event.clientX - this.pan.x);
        this.offsetY = this.pan.offsetY + (event.clientY - this.pan.y);
        this.clampOffsets();
        this.render();
        event.preventDefault();
      }
    }

    onPointerUp(event) {
      const pointer = this.pointers.get(event.pointerId);
      this.pointers.delete(event.pointerId);

      if (this.pointers.size < 2) this.pinch = null;

      // A finger lifted at the end of a pinch leaves one still down with no
      // pointerdown to follow, so hand the gesture over to a pan from where
      // that finger currently is.
      if (this.pointers.size === 1 && this.zoomed) {
        const [remaining] = [...this.pointers.values()];
        this.startPan(remaining.x, remaining.y);
      } else if (this.pointers.size === 0) {
        this.pan = null;
      }

      const wasMultiTouch = this.multiTouch;

      if (this.pointers.size === 0) {
        this.multiTouch = false;
        // a pinch that ended below the threshold snaps back to the natural size
        if (!this.zoomed && (this.scale !== 1 || this.offsetX || this.offsetY)) {
          this.animate();
          this.reset();
        }
      }

      if (!pointer || wasMultiTouch || pointer.movedBy > TAP_SLOP || event.type === 'pointercancel') {
        if (wasMultiTouch) this.lastTapAt = 0;
        return;
      }

      const now = Date.now();
      if (now - this.lastTapAt < DOUBLE_TAP_MS) {
        this.lastTapAt = 0;
        this.toggle(event.clientX, event.clientY);
      } else {
        this.lastTapAt = now;
      }
    }

    onKeyDown(event) {
      const centre = this.centre;

      switch (event.key) {
        case 'Enter':
        case ' ':
        case 'Spacebar':
          event.preventDefault();
          this.toggle(centre.x, centre.y);
          return;
        case 'Escape':
          if (!this.zoomed) return;
          event.preventDefault();
          this.animate();
          this.reset();
          return;
        case '+':
        case '=':
          event.preventDefault();
          this.animate();
          this.scaleTo(clamp(this.scale + KEY_ZOOM_STEP, 1, MAX_SCALE), centre.x, centre.y);
          return;
        case '-':
        case '_':
          event.preventDefault();
          this.animate();
          this.scaleTo(clamp(this.scale - KEY_ZOOM_STEP, 1, MAX_SCALE), centre.x, centre.y);
          return;
        default:
          break;
      }

      // arrows pan only while zoomed, so the page still scrolls otherwise
      if (!this.zoomed) return;

      const step = { ArrowLeft: [1, 0], ArrowRight: [-1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] }[event.key];
      if (!step) return;

      event.preventDefault();
      this.offsetX += step[0] * KEY_PAN_STEP;
      this.offsetY += step[1] * KEY_PAN_STEP;
      this.clampOffsets();
      this.render();
    }

    startPinch() {
      const [first, second] = [...this.pointers.values()];
      this.pinch = { distance: distance(first, second) || 1, scale: this.scale };
      this.pan = null;
    }

    startPan(clientX, clientY) {
      this.pan = { x: clientX, y: clientY, offsetX: this.offsetX, offsetY: this.offsetY };
    }

    toggle(clientX, clientY) {
      this.animate();
      if (this.zoomed) {
        this.reset();
        return;
      }
      instances.forEach((instance) => {
        if (instance !== this && instance.zoomed) instance.reset();
      });
      this.scaleTo(DOUBLE_TAP_SCALE, clientX, clientY);
    }

    // Scales about the given screen point, so whatever is under the fingers
    // stays under the fingers.
    scaleTo(scale, clientX, clientY) {
      const rect = this.frame.getBoundingClientRect();
      const pointX = clientX - (rect.left + rect.width / 2);
      const pointY = clientY - (rect.top + rect.height / 2);
      const imageX = (pointX - this.offsetX) / this.scale;
      const imageY = (pointY - this.offsetY) / this.scale;

      this.scale = scale;
      this.offsetX = pointX - imageX * scale;
      this.offsetY = pointY - imageY * scale;
      this.clampOffsets();
      this.render();
    }

    clampOffsets() {
      const rect = this.frame.getBoundingClientRect();
      const maxX = Math.max(0, (rect.width * (this.scale - 1)) / 2);
      const maxY = Math.max(0, (rect.height * (this.scale - 1)) / 2);
      this.offsetX = clamp(this.offsetX, -maxX, maxX);
      this.offsetY = clamp(this.offsetY, -maxY, maxY);
    }

    animate() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.image.classList.add('image-magnify-in_place--animating');
      window.clearTimeout(this.animationTimer);
      this.animationTimer = window.setTimeout(() => {
        this.image.classList.remove('image-magnify-in_place--animating');
      }, 250);
    }

    render() {
      this.image.style.transform = `translate3d(${this.offsetX}px, ${this.offsetY}px, 0) scale(${this.scale})`;
      this.frame.classList.toggle('is-zoomed', this.zoomed);
      this.container.classList.toggle('is-zoomed', this.zoomed);
      syncHistory(this);
      this.frame.setAttribute('aria-pressed', this.zoomed ? 'true' : 'false');
    }

    reset() {
      this.scale = 1;
      this.offsetX = 0;
      this.offsetY = 0;
      this.pan = null;
      this.render();
    }

    destroy() {
      if (this.zoomed) this.reset();
      window.clearTimeout(this.animationTimer);
      this.frame.removeEventListener('pointerdown', this.onPointerDown);
      this.frame.removeEventListener('pointermove', this.onPointerMove);
      this.frame.removeEventListener('pointerup', this.onPointerUp);
      this.frame.removeEventListener('pointercancel', this.onPointerUp);
      this.frame.removeEventListener('keydown', this.onKeyDown);
      this.frame.removeEventListener('dragstart', this.onDragStart);
      if (this.badge) this.badge.removeEventListener('click', this.onBadgeClick);
      this.frame.classList.remove('in-place-zoom', 'is-zoomed');
      this.container.classList.remove('is-zoomed');
      this.frame.removeAttribute('tabindex');
      this.frame.removeAttribute('role');
      this.frame.removeAttribute('aria-pressed');
      delete this.image.dataset.inPlaceZoom;
      instances.delete(this);
    }
  }

  function init(root) {
    (root || document).querySelectorAll(IMAGE_SELECTOR).forEach((image) => {
      if (image.dataset.inPlaceZoom) return;
      image.dataset.inPlaceZoom = 'true';
      new InPlaceZoom(image);
    });
  }

  // Variant changes and the theme editor swap gallery markup in underneath us,
  // so drop the instances whose images have left the document.
  function prune() {
    instances.forEach((instance) => {
      if (!instance.image.isConnected) instance.destroy();
    });
  }

  function refresh(root) {
    prune();
    init(root);
  }

  function resetAll() {
    instances.forEach((instance) => {
      if (instance.zoomed) instance.reset();
    });
  }

  let galleryObserver = null;

  function watchGalleries() {
    if (!('MutationObserver' in window)) return;
    if (galleryObserver) galleryObserver.disconnect();
    galleryObserver = new MutationObserver(() => refresh());
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      galleryObserver.observe(gallery, { childList: true, subtree: true });
    });
  }

  init();
  watchGalleries();

  document.addEventListener('shopify:section:load', (event) => {
    refresh(event.target);
    watchGalleries();
  });
  window.addEventListener('resize', resetAll);
  window.addEventListener('orientationchange', resetAll);

  window.addEventListener('popstate', () => {
    // our own history.back(), from closing the zoom another way
    if (unwindingHistory) {
      unwindingHistory = false;
      return;
    }
    if (!historyOwner) return;

    const owner = historyOwner;
    historyOwner = null; // cleared first, so the reset does not pop again
    owner.animate();
    owner.reset();
  });
})();
